import { useMemo, useState } from 'react'
import {
  DataSheetGrid,
  keyColumn,
  textColumn,
  type Column,
} from 'react-datasheet-grid'
import 'react-datasheet-grid/dist/style.css'
import { useTranslation } from 'react-i18next'
import { Save, X } from 'lucide-react'
import { apiErrorMessage, updateMachine } from '@/services/api'
import {
  SOFTWARE_VERSION_FIELDS,
  type ComponentDef,
  type Machine,
  type MachineInput,
  type SoftwareVersions,
} from '@/types/machine'
import MarkdownField from '@/components/MarkdownField'

// グリッドの1行 = 機材1台。値はすべて文字列で持つ
type RowData = Record<string, string>

// 一覧（MachineTable の buildColumns）と同じ列順にする。部品列の前が
// BASIC_FIELDS、ソフトバージョンの後が TRAILING_FIELDS
const BASIC_FIELDS = [
  { key: 'serialNumber', labelKey: 'machines.serial' },
  { key: 'lot', labelKey: 'detail.lot' },
  { key: 'customer', labelKey: 'machines.customer' },
  { key: 'edition', labelKey: 'machines.edition' },
  { key: 'shippedAt', labelKey: 'machines.shippedAt' },
  { key: 'supportStatus', labelKey: 'machines.supportStatus' },
  { key: 'bodySerial', labelKey: 'machines.bodySerial' },
  { key: 'bodyRecord', labelKey: 'detail.bodyRecord' },
  { key: 'bodyInspectionReport', labelKey: 'detail.bodyInspectionReport' },
] as const

const TRAILING_FIELDS = [
  { key: 'remarks', labelKey: 'detail.remarks' },
  { key: 'deviceName', labelKey: 'detail.deviceName' },
] as const

const TEXT_FIELDS = [...BASIC_FIELDS, ...TRAILING_FIELDS]

const SOFTWARE_FIELDS = SOFTWARE_VERSION_FIELDS

function toRow(machine: Machine, componentFields: ComponentDef[]): RowData {
  const row: RowData = { machineId: machine.machineId }
  for (const field of TEXT_FIELDS) {
    row[field.key] = (machine[field.key] as string | undefined) ?? ''
  }
  for (const field of SOFTWARE_FIELDS) {
    row[field.key] = machine.softwareVersions?.[field.key] ?? ''
  }
  for (const { name } of componentFields) {
    const component = machine.components.find((c) => c.name === name)
    row[`comp:${name}`] = component?.serialNumber ?? ''
    row[`note:${name}`] = component?.note ?? ''
  }
  return row
}

// グリッドの編集結果を PUT 用の入力に変換する
// （構成部品はマスタ列の S/N・メモを更新し、マスタ外の部品は維持する）
function buildInput(
  machine: Machine,
  row: RowData,
  componentFields: ComponentDef[]
): MachineInput {
  const masterNames = new Set(componentFields.map((c) => c.name))
  const existingNames = new Set(machine.components.map((c) => c.name))
  const components = machine.components.map((component) =>
    masterNames.has(component.name)
      ? {
          ...component,
          serialNumber: row[`comp:${component.name}`] || undefined,
          note: row[`note:${component.name}`] || undefined,
        }
      : component
  )
  for (const { name, category } of componentFields) {
    if (
      !existingNames.has(name) &&
      (row[`comp:${name}`] || row[`note:${name}`])
    ) {
      components.push({
        name,
        category,
        serialNumber: row[`comp:${name}`] || undefined,
        note: row[`note:${name}`] || undefined,
      })
    }
  }

  const softwareVersions: SoftwareVersions = {}
  for (const field of SOFTWARE_FIELDS) {
    softwareVersions[field.key] = row[field.key]
  }

  const input: MachineInput = {
    // 論理削除フラグはグリッドでは編集しないため既存値を引き継ぐ
    deletedAt: machine.deletedAt ?? '',
    softwareVersions,
    components,
  }
  for (const field of TEXT_FIELDS) {
    input[field.key as keyof MachineInput] = row[field.key] as never
  }
  return input
}

interface MachineEditGridProps {
  machines: Machine[]
  // 部品マスタ。構成部品の列はここから動的に作る
  componentDefs: ComponentDef[]
  onClose: () => void
  onSaved: () => void
}

export default function MachineEditGrid({
  machines,
  componentDefs,
  onClose,
  onSaved,
}: MachineEditGridProps) {
  const { t } = useTranslation()

  // 部品列は一覧と同様にコントロールボックス部品→その他の部品の順
  // （各区分内は部品マスタの順）で並べる
  const componentFields = useMemo(
    () => [
      ...componentDefs.filter((c) => c.category === 'controlBox'),
      ...componentDefs.filter((c) => c.category === 'other'),
    ],
    [componentDefs]
  )
  const machineById = useMemo(
    () => new Map(machines.map((m) => [m.machineId, m])),
    [machines]
  )
  const [originalRows] = useState<RowData[]>(() =>
    machines.map((m) => toRow(m, componentFields))
  )
  const [rows, setRows] = useState<RowData[]>(() =>
    machines.map((m) => toRow(m, componentFields))
  )
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  // 保存時に添える変更メモ（保存対象の全行に同じメモを付ける）。保存ダイアログで入力する
  const [updateNote, setUpdateNote] = useState('')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  // 変更行と、その行で変更されたセル（保存ダイアログでの差分確認用）
  const changedRows = useMemo(
    () =>
      rows
        .map((row, i) => ({
          row,
          changedKeys: Object.keys(row).filter(
            (key) => row[key] !== originalRows[i][key]
          ),
          original: originalRows[i],
        }))
        .filter(({ changedKeys }) => changedKeys.length > 0),
    [rows, originalRows]
  )

  // グリッドの列キーから表示用の列名を引く
  const columnTitle = (key: string): string => {
    const basic = TEXT_FIELDS.find((field) => field.key === key)
    if (basic) return t(basic.labelKey)
    const software = SOFTWARE_FIELDS.find((field) => field.key === key)
    if (software) return software.label
    if (key.startsWith('comp:')) return `${key.slice('comp:'.length)} S/N`
    if (key.startsWith('note:')) {
      return `${key.slice('note:'.length)} ${t('detail.componentNote')}`
    }
    return key
  }

  const columns = useMemo<Column<RowData>[]>(
    () => [
      ...BASIC_FIELDS.map((field) => ({
        ...keyColumn(field.key, textColumn),
        title: t(field.labelKey),
        minWidth: 130,
      })),
      ...componentFields.flatMap(({ name }) => [
        {
          ...keyColumn(`comp:${name}`, textColumn),
          title: `${name} S/N`,
          minWidth: 160,
        },
        {
          ...keyColumn(`note:${name}`, textColumn),
          title: `${name} ${t('detail.componentNote')}`,
          minWidth: 200,
        },
      ]),
      ...SOFTWARE_FIELDS.map((field) => ({
        ...keyColumn(field.key, textColumn),
        title: field.label,
        minWidth: 130,
      })),
      ...TRAILING_FIELDS.map((field) => ({
        ...keyColumn(field.key, textColumn),
        title: t(field.labelKey),
        minWidth: 130,
      })),
    ],
    [t, componentFields]
  )

  const handleSave = async () => {
    setSaving(true)
    const failures: string[] = []
    for (const { row } of changedRows) {
      const machine = machineById.get(row.machineId)
      if (!machine) continue
      try {
        await updateMachine(machine.machineId, {
          ...buildInput(machine, row, componentFields),
          updateNote: updateNote.trim(),
        })
      } catch (err) {
        failures.push(
          `${row.serialNumber || machine.machineId}: ${
            apiErrorMessage(err) ?? t('detail.saveError')
          }`
        )
      }
    }
    setSaving(false)
    // 失敗はダイアログを閉じてグリッド上部のバナーで表示する
    setSaveDialogOpen(false)
    setErrors(failures)
    if (failures.length === 0) {
      onSaved()
    }
  }

  const handleClose = () => {
    if (changedRows.length > 0 && !window.confirm(t('detail.confirmLeave'))) {
      return
    }
    onClose()
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm text-gray-500">{t('grid.hint')}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClose}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <X className="h-4 w-4" />
            {t('common.cancel')}
          </button>
          <button
            onClick={() => setSaveDialogOpen(true)}
            disabled={changedRows.length === 0 || saving}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saving
              ? t('detail.saving')
              : t('grid.save', { count: changedRows.length })}
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 whitespace-pre-wrap">
          {errors.join('\n')}
        </div>
      )}

      <DataSheetGrid
        value={rows}
        onChange={(newRows) => setRows(newRows as RowData[])}
        columns={columns}
        lockRows
        height={Math.min(700, 80 + rows.length * 40)}
        rowHeight={40}
      />

      {/* 保存確認ダイアログ（差分を確認して変更メモを添える） */}
      {saveDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Save className="h-5 w-5 text-gray-400" />
                {t('detail.saveDialogTitle')}
              </h2>
              <button
                onClick={() => setSaveDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto space-y-4">
              {changedRows.map(({ row, original, changedKeys }) => (
                <div key={row.machineId}>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {row.serialNumber || original.serialNumber || row.machineId}
                  </p>
                  <div className="space-y-2">
                    {changedKeys.map((key) => (
                      <div key={key}>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {columnTitle(key)}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                            <p className="text-[10px] font-medium text-red-400 mb-0.5">
                              {t('detail.saveDialogBefore')}
                            </p>
                            <p className="text-xs text-red-900 whitespace-pre-wrap break-words">
                              {original[key] || '-'}
                            </p>
                          </div>
                          <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                            <p className="text-[10px] font-medium text-green-500 mb-0.5">
                              {t('detail.saveDialogAfter')}
                            </p>
                            <p className="text-xs text-green-900 whitespace-pre-wrap break-words">
                              {row[key] || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t">
              <label className="block text-xs text-gray-600 mb-1">
                {t('detail.updateNoteLabel')}
              </label>
              <MarkdownField
                value={updateNote}
                onChange={setUpdateNote}
                placeholder={t('detail.updateNoteHint')}
              />
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setSaveDialogOpen(false)}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                {saving
                  ? t('detail.saving')
                  : t('grid.save', { count: changedRows.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
